import type { BillingConfig } from '../../operations/types/operations';
import type { AgreementParty, CollectionAgreement, CollectionOrderStep, PartyRole } from './collectionAgreementModel';
import { withAccountKind } from './agreementAccountRules';
import { newId, ReceivingAccountKind } from './collectionModel';

export type OperationKind = 'RECEIVABLES_ASSIGNMENT' | 'STRUCTURED' | 'ACKNOWLEDGEMENT_DEBT' | 'LOAN';

export interface OperationPerson {
    name: string;
    document: string;
    isActive?: boolean;
}

export interface OperationCollectionContext {
    operationKind: OperationKind;
    assignors: OperationPerson[];
    receivableAssignors: OperationPerson[];
    guarantors: OperationPerson[];
    sequencialNumber: number;
    amendmentNumber: number;
    billingConfig: BillingConfig;
    receivingAccountName?: string;
}

const MAX_ASSIGNORS = 2;

const cleanDocument = (value: string | undefined | null): string =>
    (value ?? '').replace(/[.\-/\s]/g, '').toUpperCase();

const toParty = (person: OperationPerson, role: PartyRole): AgreementParty => ({
    id: newId('party'),
    role,
    fullName: person.name.trim(),
    documentNumber: cleanDocument(person.document),
});

const distinctByDocument = (people: OperationPerson[]): OperationPerson[] => {
    const seen = new Set<string>();
    return people.filter(person => {
        const document = cleanDocument(person.document);
        if (!document || seen.has(document)) return false;
        seen.add(document);
        return true;
    });
};

const CNPJ_LENGTH = 14;
const CNPJ_ROOT_LENGTH = 8;
const HEADQUARTERS_BRANCH = '0001';

const companyKey = (document: string): string =>
    (document.length === CNPJ_LENGTH ? document.slice(0, CNPJ_ROOT_LENGTH) : document);

const isHeadquarters = (document: string): boolean =>
    document.length === CNPJ_LENGTH && document.slice(CNPJ_ROOT_LENGTH, CNPJ_ROOT_LENGTH + 4) === HEADQUARTERS_BRANCH;

const distinctCompanies = (participants: OperationPerson[], fromReceivables: OperationPerson[]): OperationPerson[] => {
    const groups = new Map<string, OperationPerson[]>();
    [...participants, ...fromReceivables].forEach(person => {
        const document = cleanDocument(person.document);
        if (!document) return;
        const key = companyKey(document);
        groups.set(key, [...(groups.get(key) ?? []), person]);
    });
    const participantDocuments = new Set(participants.map(person => cleanDocument(person.document)));
    return Array.from(groups.values()).map(people =>
        people.find(person => participantDocuments.has(cleanDocument(person.document)))
        ?? people.find(person => isHeadquarters(cleanDocument(person.document)))
        ?? people[0]);
};

const suggestedAssignors = (context: OperationCollectionContext): AgreementParty[] => {
    if (context.operationKind === 'LOAN') return [];
    return distinctCompanies(context.assignors, context.receivableAssignors)
        .slice(0, MAX_ASSIGNORS)
        .map(person => toParty(person, 'ASSIGNOR'));
};

const suggestedGuarantors = (context: OperationCollectionContext): AgreementParty[] =>
    distinctByDocument(context.guarantors.filter(person => person.isActive !== false))
        .map(person => toParty(person, 'GUARANTOR'));

const DEFAULT_STEPS: CollectionOrderStep[] = [
    { role: 'PAYER', preventive: true, overdue: true },
    { role: 'ASSIGNOR', preventive: false, overdue: true },
    { role: 'GUARANTOR', preventive: false, overdue: true },
];

const orderFor = (parties: AgreementParty[]): CollectionOrderStep[] =>
    DEFAULT_STEPS.filter(step => step.role === 'PAYER' || parties.some(party => party.role === step.role))
        .map(step => ({ ...step }));

const ESCROW_RECEIVING_ACCOUNT = 'Conta ESCROW';

export const accountKindOf = (receivingAccountName: string | null | undefined): ReceivingAccountKind =>
    (receivingAccountName === ESCROW_RECEIVING_ACCOUNT ? 'ESCROW' : 'AGROLEND');

export const contractNumberOf = (sequencialNumber: number, amendmentNumber: number): string =>
    (amendmentNumber > 0 ? `${sequencialNumber}-${amendmentNumber}` : String(sequencialNumber));

export const suggestAgreement = (context: OperationCollectionContext): CollectionAgreement => {
    const parties = [...suggestedAssignors(context), ...suggestedGuarantors(context)];
    return withAccountKind({
    id: '',
    externalReference: contractNumberOf(context.sequencialNumber, context.amendmentNumber),
    policyId: '',
    collect: true,
    collectionOrder: orderFor(parties),
    receivingAccountKind: 'AGROLEND',
    payment: { instruction: context.billingConfig.generate_billets === false ? 'TRANSFER' : 'BOLETO' },
    parties,
    notes: null,
}, accountKindOf(context.receivingAccountName));
};

export const billingConfigFromAgreement = (agreement: CollectionAgreement): BillingConfig => ({
    generate_billets: agreement.payment.instruction === 'BOLETO',
    automatic_preventive_collection: agreement.collectionOrder.some(step => step.preventive),
    automatic_overdue_collection: agreement.collectionOrder.some(step => step.overdue),
});

export const syncWithOperation = (draft: CollectionAgreement, suggested: CollectionAgreement): CollectionAgreement => ({
    ...draft,
    parties: suggested.parties,
    collectionOrder: suggested.collectionOrder.map(step =>
        draft.collectionOrder.find(current => current.role === step.role) ?? step),
});

export interface DocumentAssignor {
    documentId: string;
    assignorName: string | null;
    assignorCnpj: string | null;
}

export const assignorsFromDocuments = (documents: DocumentAssignor[]): OperationPerson[] => documents
    .filter(document => Boolean((document.assignorCnpj ?? '').trim()))
    .map(document => ({ name: (document.assignorName ?? '').trim(), document: (document.assignorCnpj ?? '').trim() }));
